import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { isEmpty, omit, sumBy, uniqueId } from "lodash";

import SelectField from "Components/Common/SelectField";
import InputFieldText from "Components/Common/InputFieldText";
import { Text } from "Components/MeterInfoForm/ConsumerDetails/styles";
import Modal from "Components/Common/Modal";
import Backdrop from "Components/Common/Backdrop";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";

import { formatUtilityAddress } from "utils/formatUtilityAddress";
import { filterEmptyStrings, getImageUrl } from "utils";

import {
  useLazyGetDebitNotesDetailsByDebitNoteIdQuery,
  usePostDebitNoteDetailsMutation,
  useUtilityConsumerContactInfoQuery,
} from "apis/UtilityConsumer/utilityConsumer";

const DebitNoteForm = ({ t, editModalProps, handleClose }) => {
  const { utilityConsumerId: currentConsumerId, id } = editModalProps ?? {};

  const { data: { data: utilityConsumersOptions } = {} } =
    useUtilityConsumerContactInfoQuery();

  const [
    getDebitNoteDetails,
    { data: { data: debitNoteData } = {}, isLoading: isDebitNoteDataLoading },
  ] = useLazyGetDebitNotesDetailsByDebitNoteIdQuery();

  const [addDebitNoteDetails, { isLoading: isDebitNoteDetailsAdding }] =
    usePostDebitNoteDetailsMutation();

  const [documentToPreview, setDocumentToPreview] = useState(null);

  const formik = useFormik({
    enableReinitialize: !!currentConsumerId,
    initialValues: {
      id: id ?? 0,
      utilityConsumerId: currentConsumerId ?? "",
      consumerDetails: {
        utilityAddress: debitNoteData?.utilityConsumer?.utilityAddress ?? "",
        tariff: debitNoteData?.utilityConsumer?.tariff ?? "",
        consumerName: debitNoteData?.utilityConsumer?.consumerName ?? "",
        consumerNumber: debitNoteData?.utilityConsumer?.consumerNumber ?? "",
        isTemporaryConnection:
          debitNoteData?.utilityConsumer?.isTemporaryConnection ?? false,
      },
      debitNoteModels: debitNoteData?.debitNoteModels ?? [
        {
          id: uniqueId("temp_"),
          chargeType: "",
          additionalSecurityDeposit: 0,
          additionalConnectionCharges: 0,
          testReportFee: 0,
          totalPayable: 0,
          otherChargesAndDescription: [],
        },
      ],
    },
    validationSchema: Yup.object().shape({
      utilityConsumerId: Yup.string().required("Consumer number is required"),
      debitNoteModels: Yup.array().of(
        Yup.object().shape({
          chargeType: Yup.string().required("Charge type is required"),
          additionalSecurityDeposit: Yup.number().test(
            "isValidNumber",
            "Additional security deposit must be positive",
            (value) => value >= 0
          ),
          additionalConnectionCharges: Yup.number().test(
            "isValidNumber",
            "Additional connection charges must be positive",
            (value) => value >= 0
          ),
          testReportFee: Yup.number().test(
            "isValidNumber",
            "Test report fee must be positive",
            (value) => value >= 0
          ),
          totalPayable: Yup.number().test(
            "isValidNumber",
            "Total payable must be positive",
            (value) => value >= 0
          ),
          otherChargesAndDescription: Yup.array().of(
            Yup.object().shape({
              description: Yup.string().required("Description is required"),
              chargeAmount: Yup.number().test(
                "isValidNumber",
                "Charge amount must be positive",
                (value) => value >= 0
              ),
            })
          ),
        })
      ),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = omit(
          {
            ...values,
            debitNoteModels: values.debitNoteModels.map((item) => ({
              ...item,
              id: item.id.toString().startsWith("temp") ? 0 : item.id,
            })),
          },
          "consumerDetails"
        );

        const {
          data: { url },
        } = await addDebitNoteDetails(filterEmptyStrings(payload));

        setDocumentToPreview({
          url: getImageUrl(url),
          documentTypeName: "Debit Note Document",
          documentType: "pdf",
          documentName: "Debit Note Document",
        });
        resetForm();
      } catch (err) {
        console.error(err);
      }
    },
  });

  const {
    handleSubmit,
    values: {
      debitNoteModels,
      utilityConsumerId,
      consumerDetails: {
        consumerName,
        tariff,
        utilityAddress,
        consumerNumber,
        isTemporaryConnection,
      },
    },
    setFieldValue,
    setFieldTouched,
  } = formik;

  const handleAddCharges = (existingCharges) => {
    const addNewCharge = {
      id: uniqueId("temp_"),
      chargeType: "",
      additionalSecurityDeposit: 0,
      additionalConnectionCharges: 0,
      testReportFee: 0,
      totalPayable: 0,
      otherChargesAndDescription: [],
    };
    setFieldValue("debitNoteModels", [...existingCharges, addNewCharge]);
  };

  const handleRemoveClick = (i) => {
    if (debitNoteModels.length > 1) {
      setFieldValue(
        "debitNoteModels",
        debitNoteModels.filter((_, index) => index !== i)
      );
      setFieldTouched(`debitNoteModels[${i}]`, false, false);
    }
  };

  const onInputValueChange = (keyName, value, index, totalPayable) => {
    setFieldValue(keyName, !isEmpty(value) ? +value : "");
    setFieldValue(`debitNoteModels.${index}.totalPayable`, totalPayable);
  };

  useEffect(() => {
    (async () => {
      try {
        if (id) {
          await getDebitNoteDetails(id);
        }
      } catch (error) {}
    })();
  }, [id]);

  if (isDebitNoteDataLoading) {
    return <Backdrop open />;
  }

  return (
    <Modal
      isOpen
      handleClose={handleClose}
      maxWidth="lg"
      title={`${!currentConsumerId ? "Add" : "Edit"} Debit Note Details`}
    >
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => {
            setDocumentToPreview(null);
            handleClose();
          }}
          {...documentToPreview}
        />
      )}

      <Grid container className="mt-3" columnSpacing={2}>
        {!currentConsumerId && (
          <Grid container item className="d-flex justify-content-between">
            <Grid item xs={12} sm={6}>
              <SelectField
                label={t("Consumer Number") + "*"}
                name="utilityConsumerId"
                formik={formik}
                options={utilityConsumersOptions || []}
                onChange={(_, { value }) => {
                  const selectedOption = utilityConsumersOptions.find(
                    ({ id }) => value === id
                  );
                  setFieldValue("utilityConsumerId", value);
                  setFieldValue("consumerDetails", {
                    tariff: selectedOption.tariff,
                    consumerName: selectedOption.consumerName,
                    utilityAddress: selectedOption.utilityAddress,
                    consumerNumber: selectedOption.consumerNumber,
                    isTemporaryConnection: selectedOption.isTemporaryConnection,
                  });
                }}
                getOptionLabel={({ consumerNumber }) => consumerNumber}
                getOptionValue={({ id }) => id}
              />
            </Grid>
          </Grid>
        )}

        {!!utilityConsumerId && (
          <Grid
            container
            item
            className="border border-2 mt-2 ps-2 pb-0 ms-2 p-3 mb-2"
          >
            <Grid item xs={12} sm={6}>
              <Text>
                <b>{t("Consumer Name")}: </b>
                {consumerName}
              </Text>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text>
                <b>{t("Consumer Number")}: </b>
                {consumerNumber}
              </Text>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text>
                <b>{t("Tariff")}: </b>
                {tariff}
              </Text>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text>
                <b>{t("Utility Address")}: </b>
                {`${formatUtilityAddress(utilityAddress)}, Gandhinagar-382050`}
              </Text>
            </Grid>

            {debitNoteModels.length &&
              debitNoteModels.map((data, index) => (
                <Grid
                  key={data.id}
                  container
                  columnSpacing={{ xs: 1 }}
                  className="border border-2 ps-2 pb-0 p-3 ms-1 mb-2"
                >
                  <Grid item xs={12} sm={6} md={4}>
                    <InputFieldText
                      label={t("Charge Type") + "*"}
                      name={`debitNoteModels.${index}.chargeType`}
                      formik={formik}
                      placeholder={t("Enter Charge Type") + "*"}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InputFieldText
                      label={
                        t(
                          isTemporaryConnection
                            ? "Additional Temporary Connection Deposit"
                            : "Additional Security Deposit"
                        ) + "*"
                      }
                      name={`debitNoteModels.${index}.additionalSecurityDeposit`}
                      onChange={(e) =>
                        onInputValueChange(
                          `debitNoteModels.${index}.additionalSecurityDeposit`,
                          e.target.value,
                          index,
                          +e.target.value +
                            data.additionalConnectionCharges +
                            data.testReportFee +
                            sumBy(
                              data.otherChargesAndDescription,
                              "chargeAmount"
                            )
                        )
                      }
                      type="number"
                      formik={formik}
                      placeholder={t("Enter Additional Security Deposit")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InputFieldText
                      label={
                        t(
                          isTemporaryConnection
                            ? "Additional Supervision charges"
                            : "Additional Connection Charges"
                        ) + "*"
                      }
                      type="number"
                      name={`debitNoteModels.${index}.additionalConnectionCharges`}
                      onChange={(e) =>
                        onInputValueChange(
                          `debitNoteModels.${index}.additionalConnectionCharges`,
                          e.target.value,
                          index,
                          +e.target.value +
                            data.additionalSecurityDeposit +
                            data.testReportFee +
                            sumBy(
                              data.otherChargesAndDescription,
                              "chargeAmount"
                            )
                        )
                      }
                      formik={formik}
                      placeholder={t("Enter Additional Connection Charges")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InputFieldText
                      label={t("Test Report Fee") + "*"}
                      type="number"
                      name={`debitNoteModels.${index}.testReportFee`}
                      onChange={(e) =>
                        onInputValueChange(
                          `debitNoteModels.${index}.testReportFee`,
                          e.target.value,
                          index,
                          +e.target.value +
                            data.additionalConnectionCharges +
                            data.additionalSecurityDeposit +
                            sumBy(
                              data.otherChargesAndDescription,
                              "chargeAmount"
                            )
                        )
                      }
                      formik={formik}
                      placeholder={t("Enter Test Report Fee")}
                    />
                  </Grid>

                  <Grid item>
                    <Button
                      color="primary"
                      size="small"
                      sx={{
                        minWidth: "unset",
                        border: "1px solid",
                        height: "40px",
                        width: "100px",
                        marginBottom: 2,
                        marginRight: 2,
                      }}
                      onClick={() =>
                        setFieldValue(
                          `debitNoteModels.${index}.otherChargesAndDescription`,
                          [
                            ...data.otherChargesAndDescription,
                            {
                              description: "",
                              chargeAmount: 0,
                            },
                          ]
                        )
                      }
                    >
                      <strong className="d-flex justify-content-center">
                        <AddIcon fontSize="small" />
                        {t("Add")}
                      </strong>
                    </Button>
                  </Grid>
                  {data.otherChargesAndDescription.map((_, i) => (
                    <Grid container item columnSpacing={2} key={`index-${i}`}>
                      <Grid item xs={12} sm={6} md={4}>
                        <InputFieldText
                          label={t("Description") + "*"}
                          name={`debitNoteModels.${index}.otherChargesAndDescription.${i}.description`}
                          formik={formik}
                          placeholder={t("Enter Description") + "*"}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <InputFieldText
                          label={t("Additional chargeAmount") + "*"}
                          name={`debitNoteModels.${index}.otherChargesAndDescription.${i}.chargeAmount`}
                          onChange={(e) =>
                            onInputValueChange(
                              `debitNoteModels.${index}.otherChargesAndDescription.${i}.chargeAmount`,
                              e.target.value,
                              index,
                              +e.target.value +
                                data.additionalConnectionCharges +
                                data.additionalSecurityDeposit +
                                data.testReportFee +
                                sumBy(
                                  data.otherChargesAndDescription,
                                  "chargeAmount"
                                ) -
                                data.otherChargesAndDescription[i].chargeAmount
                            )
                          }
                          type="number"
                          formik={formik}
                          placeholder={t("Enter Additional chargeAmount") + "*"}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <IconButton
                          onClick={() => {
                            const filteredData =
                              data.otherChargesAndDescription.filter(
                                (_, inx) => i !== inx
                              );
                            setFieldValue(
                              `debitNoteModels.${index}.otherChargesAndDescription`,
                              filteredData
                            );
                            setFieldValue(
                              `debitNoteModels.${index}.totalPayable`,
                              data.additionalConnectionCharges +
                                data.additionalSecurityDeposit +
                                data.testReportFee +
                                sumBy(filteredData, "chargeAmount")
                            );
                          }}
                        >
                          <CloseIcon color="error" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}

                  <Grid item xs={12} className="d-flex justify-content-between">
                    <div>
                      {debitNoteModels.length > 1 && (
                        <Button
                          color="error"
                          size="small"
                          sx={{
                            minWidth: "unset",
                            border: "1px solid",
                            height: "40px",
                            width: "100px",
                            mb: 2,
                          }}
                          onClick={() => handleRemoveClick(index)}
                        >
                          <strong className="d-flex justify-content-center">
                            <DeleteOutlineSharpIcon fontSize="small" />
                            {t("Remove")}
                          </strong>
                        </Button>
                      )}
                    </div>
                    <Typography>
                      <b>Total</b> : {debitNoteModels[index].totalPayable}
                    </Typography>
                  </Grid>
                </Grid>
              ))}

            <Grid
              item
              xs={12}
              className="d-flex justify-content-between mb-2 mt-2"
            >
              <Button
                color="primary"
                size="small"
                onClick={() => handleAddCharges(debitNoteModels)}
                sx={{ minWidth: "unset", textTransform: "capitalize" }}
                variant="outlined"
              >
                <strong>
                  <span>+</span> {t("Add Charge")}
                </strong>
              </Button>

              <Button
                size="medium"
                variant="contained"
                onClick={handleSubmit}
                disabled={isDebitNoteDetailsAdding}
                endIcon={
                  isDebitNoteDetailsAdding && <CircularProgress size={20} />
                }
              >
                {t("Submit")}
              </Button>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Modal>
  );
};

DebitNoteForm.propTypes = {
  t: PropTypes.func,
  editModalProps: PropTypes.object,
  handleClose: PropTypes.func,
};

export default withTranslation()(DebitNoteForm);
